'use client';

import { useState, useEffect } from 'react';
import type { TocItem } from '@/types/table-of-contents';

interface MobileStickyTocProps {
  items: TocItem[];
}

export default function MobileStickyToc({ items }: MobileStickyTocProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeId, setActiveId] = useState<string>('');
  const [isVisible, setIsVisible] = useState(false);

  // Track scroll position to show/hide the sticky ToC
  useEffect(() => {
    const handleScroll = () => {
      // Show when scrolled past the article header (approximately 500px)
      const scrollThreshold = 500;
      setIsVisible(window.scrollY > scrollThreshold);
    };

    handleScroll(); // Check initial position
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Track active heading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '0% 0% -80% 0%' },
    );

    // Observe all headings
    items.forEach((item) => {
      const element = document.getElementById(item.slug);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [items]);

  if (!items || items.length === 0) {
    return null;
  }

  const handleItemClick = (slug: string) => {
    const element = document.getElementById(slug);
    if (element) {
      const offset = 80 + 56; // header height + sticky toc height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
    setIsExpanded(false);
  };

  return (
    <div className="lg:hidden">
      {/* Sticky bar - only visible after scrolling */}
      <div
        className={`fixed top-16 left-0 right-0 z-20 bg-zinc-100 border-b border-zinc-200 shadow-sm transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ height: '56px' }}
      >
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full h-full flex items-center justify-between px-4 sm:px-6 text-left hover:bg-zinc-150 transition-colors"
          aria-expanded={isExpanded}
          aria-label="Toggle table of contents"
        >
          <span className="text-sm font-medium text-zinc-700">In This Article</span>
          {isExpanded ? (
            // X icon when expanded
            <svg
              className="w-5 h-5 text-zinc-600 transition-all duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            // Chevron down when collapsed
            <svg
              className="w-5 h-5 text-zinc-600 transition-all duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Expanded state - overlay */}
      {isExpanded && isVisible && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-20"
            style={{ top: '120px' }}
            onClick={() => setIsExpanded(false)}
            aria-hidden="true"
          />

          {/* ToC content */}
          <div
            className="fixed left-0 right-0 z-30 bg-white border-b border-zinc-200 shadow-lg overflow-y-auto"
            style={{ top: '120px', maxHeight: 'calc(100vh - 120px)' }}
          >
            <div className="px-4 sm:px-6 py-4">
              {/* ToC items - no header needed, it's visible in the toggle bar */}
              <ul className="space-y-2 text-sm pb-4">
                {items.map((item) => {
                  const isActive = activeId === item.slug;
                  const levelClass = item.level === 3 ? 'pl-4' : '';

                  return (
                    <li key={item.slug} className={levelClass}>
                      <button
                        onClick={() => handleItemClick(item.slug)}
                        className={`block py-2 px-3 w-full text-left transition-colors duration-200 border-l-2 rounded-r ${
                          isActive
                            ? 'border-black text-black font-medium bg-zinc-50'
                            : 'border-transparent text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 hover:bg-zinc-50'
                        }`}
                      >
                        {item.text}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
